import React, { useState, useEffect } from "react";

import { Cell, Pie, PieChart } from "recharts";
import { getConnectionData } from "../../../util/Scripts";
import { getForm } from "../../../services/restAPI/RestApi";
import { Spin } from "../../BP2Components";

import ProjectSummarySettingsModal from "../WidgetsSettings/ProjectSummary/ProjectSummarySettingsModal";
import projectConfig from "@projectConfig";
import SettingsModal from "../../Modal/SettingsModal";
import IntlMessages from "../../../util/IntlMessages";
import Widget from "../../Widget/Widget";

import "./projectSummary.less";
import { getFormWithData } from "../../../services/restAPI/FormsV3";

const ProjectSummaryWidget = ({ value, role }) => {
   const [mobileView, setMobileView] = useState();
   const [elements, setElements] = useState({});
   const [loading, setLoading] = useState(true);
   const [pieData, setPieData] = useState([
      { name: "done", value: 0 },
      { name: "left", value: 0 },
   ]);

   const raw = (data) => data?.value?.originalValue ?? data?.value;

   const hoursUsed = Number(raw(elements?.[value?.hoursUsed]));
   const hourlyBudget = Number(raw(elements?.[value?.hourlyBudget]));

   const pieChartSize = () => {
      return mobileView ? 120 : 180;
   };

   useEffect(() => {
      const resize = () => {
         setMobileView(() => window.matchMedia("(max-width: 1199px)").matches);
      };

      window.addEventListener("resize", resize);

      resize();

      return () => window.removeEventListener("resize", resize);
   }, []);

   //I don't want float numbers in piechart, that's why I'm using parseInt
   useEffect(() => {
      setPieData([
         { name: "done", value: parseInt(raw(elements?.[value?.progressInProject])) || 0 },
         { name: "left", value: 100 - parseInt(raw(elements?.[value?.progressInProject])) || 0 },
      ]);
   }, [elements, value]);

   useEffect(() => {
      const connectionsData = getConnectionData(value.id);
      const formId = value?.formId;
      const recordId = connectionsData ?? value?.recordId;

      if (!formId || !recordId) {
         setLoading(true);
         return;
      }

      setLoading(true);

      const isV3 = formId.length > 10;
      const API = isV3
         ? getFormWithData(formId, recordId)
         : getForm(formId, recordId, null, null, null, true);
      API.then((res) => {
         if (isV3) {
            const elementsData = (res?.data.fields || []).reduce((acc, item) => {
               if (item && item.id) acc[String(item.id)] = item;
               return acc;
            }, {});
            setElements(elementsData);
         } else {
            setElements(res?.data?.default?.mainForm?.forms_data?.tab?.[0]?.elements);
         }
      })
         .catch((err) => {
            console.error(err);
         })
         .finally(() => {
            setLoading(false);
         });
   }, [value.formId, value.recordId, window.location.search]);

   return (
      <Widget
         className="Widget"
         role={role}
         extra={
            role === "OWNER" || role === "MODERATOR" ? (
               <SettingsModal
                  widgetName={value?.name}
                  widgetId={value?.id}
                  icon="setting"
                  deleteIcon="true"
                  children={
                     <ProjectSummarySettingsModal value={value} role={role} elements={elements} />
                  }
               />
            ) : null
         }
      >
         <div className="project_summary-container">
            {loading ? (
               <div id="items-loader" className="items-loader">
                  <Spin />
               </div>
            ) : (
               <>
                  <h4 className="project_summary-project_name">
                     {raw(elements?.[value?.projectName])}
                  </h4>
                  <div className="project_summary-progress_in_completing_the_project">
                     <div className="project_summary-category_title">
                        <i className={`icon icon-star-half gx-mr-3 gx-mt-2`} />
                        <h5>
                           <IntlMessages id="projectSummary.progress_in_completing_the_project" />
                        </h5>
                     </div>
                     <div className="project_summary-progress_chart_container">
                        <h2 className="project_summary-progress_value">
                           {((x) => (Number.isFinite(x) ? `${Math.round(x)}%` : null))(
                              Number(
                                 String(raw(elements?.[value?.progressInProject]) ?? "").replace(
                                    ",",
                                    ".",
                                 ),
                              ),
                           )}
                        </h2>
                        <PieChart width={pieChartSize()} height={pieChartSize()}>
                           <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              outerRadius={pieChartSize() * 0.45}
                              innerRadius={pieChartSize() * 0.3}
                              fill="#8884d8"
                              dataKey="value"
                           >
                              <Cell key={`cell-0`} fill={projectConfig.cssVariables["--third"]} />
                              <Cell key={`cell-1`} fill={projectConfig.cssVariables["--first"]} />
                           </Pie>
                        </PieChart>
                     </div>
                  </div>

                  <div className="project_summary-data">
                     <div className="project_summary-start_of_the_project">
                        <div className="project_summary-category_title">
                           <i className={`icon icon-birthday-new gx-mr-3 gx-mt-2`} />
                           <h5>
                              <IntlMessages id="projectSummary.start_of_the_project" />
                           </h5>
                        </div>
                        <h6>{raw(elements?.[value?.startProject])?.split(" ")[0] ?? ""}</h6>
                     </div>
                     <div className="project_summary-end_of_the_project">
                        <div className="project_summary-category_title">
                           <i className={`icon icon-diamond gx-mr-3 gx-mt-2`} />
                           <h5>
                              <IntlMessages id="projectSummary.end_of_the_project" />
                           </h5>
                        </div>

                        <h6>{raw(elements?.[value?.endProject])?.split(" ")[0] ?? ""}</h6>
                     </div>
                     <div className="project_summary-hourly_budget">
                        <div className="project_summary-category_title ">
                           <i className={`icon icon-pricing-table gx-mr-3 gx-mt-2`} />
                           <h5>
                              <IntlMessages id="projectSummary.hourly_budget" />
                           </h5>
                        </div>

                        <h6>
                           {Number.isFinite(hourlyBudget) ? `${hourlyBudget.toFixed(2)} h` : null}
                        </h6>
                     </div>
                     <div className="project_summary-hours_used">
                        <div className="project_summary-category_title">
                           <i className={`icon icon-schedule gx-mr-3 gx-mt-2`} />
                           <h5>
                              <IntlMessages id="projectSummary.hours_used" />
                           </h5>
                        </div>
                        <h6>{Number.isFinite(hoursUsed) ? `${hoursUsed.toFixed(2)} h` : null}</h6>
                     </div>
                  </div>
               </>
            )}
         </div>
      </Widget>
   );
};

export default ProjectSummaryWidget;
